import { Route } from '@angular/router';
import { authGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { RouteIdResolveService } from '@core/services/route-id-resolve.service';
import { AdminGuard } from '@core/auth/guards/admin.guard';

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */

export const appRoutes: Route[] = [
    { path: '', pathMatch: 'full', redirectTo: 'dashboard' },

    // Redirect signed in user to the '/dashboard'
    //
    // After the user signs in, the sign in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    { path: 'signed-in-redirect', pathMatch: 'full', redirectTo: 'dashboard' },

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'confirmation-required',
                loadChildren: () =>
                    import(
                        'app/modules/auth/confirmation-required/confirmation-required.module'
                    ).then((m) => m.AuthConfirmationRequiredModule),
            },
            {
                path: 'forgot-password',
                loadChildren: () =>
                    import(
                        'app/modules/auth/forgot-password/forgot-password.module'
                    ).then((m) => m.AuthForgotPasswordModule),
            },
            {
                path: 'reset-password',
                loadChildren: () =>
                    import(
                        'app/modules/auth/reset-password/reset-password.module'
                    ).then((m) => m.AuthResetPasswordModule),
            },
            {
                path: 'sign-in',
                loadChildren: () =>
                    import('app/modules/auth/sign-in/sign-in.module').then(
                        (m) => m.AuthSignInModule
                    ),
            },
            // TODO: used it for later
            // {
            //     path: 'sign-up',
            //     loadChildren: () =>
            //         import('app/modules/auth/sign-up/sign-up.module').then(
            //             (m) => m.AuthSignUpModule
            //         ),
            // },
        ],
    },

    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [authGuard],
        canActivateChild: [authGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'sign-out',
                loadChildren: () =>
                    import('app/modules/auth/sign-out/sign-out.module').then(
                        (m) => m.AuthSignOutModule
                    ),
            },
            {
                path: 'unlock-session',
                loadChildren: () =>
                    import(
                        'app/modules/auth/unlock-session/unlock-session.module'
                    ).then((m) => m.AuthUnlockSessionModule),
            },
        ],
    },

    // Import
    {
        path: 'import-process',
        canActivate: [authGuard],
        canActivateChild: [authGuard],
        component: LayoutComponent,
        data: {
            breadcrumb: 'import.process',
        },
        children: [
            {
                path: 'import-management',
                children: [
                    {
                        path: '',
                        loadChildren: () =>
                            import(
                                '@pages/import-process/import-management/import-management.module'
                            ).then((m) => m.ImportManagementModule),
                    },
                    {
                        path: 'view/:importId',
                        loadChildren: () =>
                            import(
                                '@pages/import-process/import-management/components/import-information/import-information.module'
                            ).then((m) => m.ImportInformationModule),
                    },
                ],
            },
            {
                path: 'export-management',
                children: [
                    {
                        path: '',
                        loadChildren: () =>
                            import(
                                '@pages/import-process/export-management/export-management.module'
                            ).then((m) => m.ExportManagementModule),
                    },
                    {
                        path: 'view/:exportId',
                        loadChildren: () =>
                            import(
                                '@pages/import-process/export-management/components/export-information/export-information.module'
                            ).then((m) => m.ExportInformationModule),
                    },
                ],
            },
            {
                path: 'create-import',
                loadChildren: () =>
                    import(
                        '@pages/import-process/order-import-coupon/create-import-coupon.module'
                    ).then((m) => m.CreateImportCouponModule),
            },
            {
                path: 'view/:importId',
                loadChildren: () =>
                    import(
                        '@pages/import-process/order-import-coupon/create-import-coupon.module'
                    ).then((m) => m.CreateImportCouponModule),
            },
            {
                path: 'create-export',
                loadChildren: () =>
                    import(
                        '@pages/import-process/create-export-coupon/create-export-coupon.module'
                    ).then((m) => m.CreateExportCouponModule),
            },
        ],
    },
    {
        path: 'create-trans',
        canActivate: [authGuard],
        canActivateChild: [authGuard],
        component: LayoutComponent,
        children: [
            {
                path: 'cn',
                pathMatch: 'full',
                loadChildren: () =>
                    import(
                        'app/modules/admin/page/create-export-item-cn/create-export-item-cn.module'
                    ).then((m) => m.CreateExportItemCnModule),
            },
            // {
            //     path: 'vn',
            //     loadChildren: () =>
            //         import(
            //             'app/modules/admin/page/stock-management/stock-management-form/stock-management-form.module'
            //         ).then((m) => m.StockManagementFormModule),
            // }
        ],
    },

    // Stock management
    {
        path: 'stock-management',
        canActivate: [authGuard],
        canActivateChild: [authGuard],
        component: LayoutComponent,
        data: {
            breadcrumb: 'Quản lý kho',
        },
        children: [
            {
                path: '',
                pathMatch: 'full',
                loadChildren: () =>
                    import(
                        'app/modules/admin/page/stock-management/stock-management.module'
                    ).then((m) => m.StockManagementModule),
            },
            {
                path: 'create',
                loadChildren: () =>
                    import(
                        'app/modules/admin/page/stock-management/stock-management-form/stock-management-form.module'
                    ).then((m) => m.StockManagementFormModule),
                data: {
                    breadcrumb: 'Thêm mới kho',
                },
            },
            {
                path: 'view/:stockId',
                pathMatch: 'full',
                loadChildren: () =>
                    import(
                        'app/modules/admin/page/stock-management/stock-management-form/stock-management-form.module'
                    ).then((m) => m.StockManagementFormModule),
                data: {
                    breadcrumb: 'Chi tiết kho',
                },
            },
        ],
    },
    {
        path: 'vehicle-management',
        canActivate: [authGuard],
        canActivateChild: [authGuard],
        component: LayoutComponent,
        data: {
            breadcrumb: 'Quản lý xe',
        },
        children: [
            {
                path: '',
                pathMatch: 'full',
                loadChildren: () =>
                    import(
                        'app/modules/admin/page/manage-vehicle/manage-vehicle.module'
                    ).then((m) => m.ManageVehicleModule),
            },
        ],
    },
    {
        path: 'customer-management',
        canActivate: [authGuard],
        canActivateChild: [authGuard],
        component: LayoutComponent,
        data: {
            breadcrumb: 'customer.management',
        },
        children: [
            {
                path: '',
                pathMatch: 'full',
                loadChildren: () =>
                    import(
                        'app/modules/admin/page/customer-management/customer-management.module'
                    ).then((m) => m.CustomerManagementModule),
            },
            {
                path: 'create',
                loadChildren: () =>
                    import(
                        'app/modules/admin/page/customer-management/page/control-customer.module'
                    ).then((m) => m.ControlCustomerModule),
            },
            {
                path: ':id',
                loadChildren: () =>
                    import(
                        'app/modules/admin/page/customer-management/page/control-customer.module'
                    ).then((m) => m.ControlCustomerModule),
                data: {
                    breadcrumb: `Chi tiết khách hàng`,
                },
            },
        ],
    },
    {
        path: 'setting',
        canActivate: [authGuard],
        canActivateChild: [authGuard],
        component: LayoutComponent,
        // resolve: {
        //     initialData: InitialDataResolver,
        // },
        children: [
            {
                path: '',
                pathMatch: 'full',
                loadChildren: () =>
                    import(
                        'app/modules/admin/page/setting/setting.module'
                    ).then((m) => m.SettingModule),
            },
        ],
    },

    {
        path: 'order',
        canActivate: [authGuard],
        canActivateChild: [authGuard],
        component: LayoutComponent,
        data: { breadcrumb: 'shipping.process' },
        children: [
            {
                path: 'new',
                pathMatch: 'full',
                loadChildren: () =>
                    import(
                        'app/modules/admin/page/create-order/create-order.module'
                    ).then((m) => m.CreateOrderModule),
            },
            {
                path: 'order-management',
                pathMatch: 'full',
                loadChildren: () =>
                    import(
                        'app/modules/admin/page/order-internal-manage/order-internal-manage.module'
                    ).then((m) => m.OrderInternalManageModule),
            },
            {
                path: 'deliver',
                pathMatch: 'full',
                loadChildren: () =>
                    import(
                        'app/modules/admin/page/deliver-order/deliver-order.module'
                    ).then((m) => m.DeliverOrderModule),
            },

            {
                path: 'export-management',
                pathMatch: 'full',
                loadChildren: () =>
                    import(
                        'app/modules/admin/page/manage-export-internal/manage-export-internal.module'
                    ).then((m) => m.ManageExportInternalModule),
            },
            {
                path: 'order-management/:id',
                pathMatch: 'full',
                loadChildren: async () => {
                    const mViewOrderInternal = await import(
                        'app/modules/admin/page/view-order-internal/view-order-internal.module'
                    );
                    return mViewOrderInternal.ViewOrderInternalModule;
                },
                data: {
                    breadcrumb: (data: any) => {
                        return `${data.user}`;
                    },
                },
                resolve: { user: RouteIdResolveService },
            },
            {
                path: 'export-management/:id',
                pathMatch: 'full',
                loadChildren: async () => {
                    const mViewOrderInternal = await import(
                        'app/modules/admin/page/detail-export-internal/detail-export-internal.module'
                    );
                    return mViewOrderInternal.DetailExportInternalModule;
                },
                // data: {
                //     breadcrumb: (data: any) => {
                //         return `${data.user}`;
                //     },
                // },
                // resolve: { user: RouteIdResolveService },
            },
        ],
    },

    // Admin routes
    {
        path: '',
        canActivate: [authGuard],
        canActivateChild: [authGuard],
        component: LayoutComponent,
        // resolve: {
        //     initialData: InitialDataResolver,
        // },
        children: [
            {
                path: 'dashboard',
                loadChildren: () =>
                    import(
                        'app/modules/admin/page/dashboard/dashboard.module'
                    ).then((m) => m.DashboardModule),
            },
        ],
    },

    {
        path: 'audit-log',
        canActivate: [authGuard, AdminGuard],
        canActivateChild: [authGuard, AdminGuard],
        component: LayoutComponent,
        children: [
            {
                path: '',
                loadChildren: () =>
                    import(
                        'app/modules/admin/page/audit-log/audit-log.module'
                    ).then((m) => m.AuditLogModule),
            },
        ],
    },

    {
        path: 'user-management',
        canActivate: [authGuard, AdminGuard],
        canActivateChild: [authGuard, AdminGuard],
        component: LayoutComponent,
        children: [
            {
                path: '',
                loadChildren: () =>
                    import(
                        'app/modules/admin/page/user-management/user-management.module'
                    ).then((m) => m.UserManagementModule),
            },
        ],
    },

    {
        path: '403',
        component: LayoutComponent,
        children: [
            {
                path: '',
                loadChildren: () =>
                    import(
                        'app/modules/admin/page/forbidden/forbidden.module'
                    ).then((m) => m.ForbiddenModule),
            },
        ],
    },

    {
        path: 'update/:id',
        component: LayoutComponent,
        canActivate: [authGuard],
        canActivateChild: [authGuard],
        children: [
            {
                path: '',
                loadChildren: () =>
                    import(
                        'app/shared/update-order-mobie/update-order-mobie.module'
                    ).then((m) => m.UpdateOrderMobieModule),
            },
        ],
    },
];
